import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";
import { objectTypesConfigToArray } from "../../../../../utils/objectTypesConfigToArray";
import IconTitle from "../../../../shared/components/IconTitle";
import MainButton from "../../../../shared/components/MainButton";
import Text from "../../../../shared/components/Text";
import {
  employmentStatusTypesConfig,
  employmentStatusTypeWithoutOtherConfig,
  PROFESSIONAL_POSITION_FILTER,
  professionalPositionTypesForCivilServantConfig,
  professionalPositionTypesForNonCivilServantConfig,
} from "../config";
import { assetIds, InputDate, ThemedAsset } from "nemuru-components";
import { ConfigContext } from "../../../../../contexts/ConfigContext";
import { netIncomeTypesConfig } from "../../../income/DeclaredIncome/config";
import { validateKeyboardInputDate } from "../../../../../utils/validations";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { FormatDateMapper } from "../../../../../utils/mappers/FormatDateMapper";

export default function ProfessionalDataEmploymentForm({
  onSubmitForm,
  data,
  netIncomeType,
  disableOthers,
}) {
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down("xs"));
  const {
    state: { participant },
  } = useContext(ProcessContext);
  const rawBirthDate =
    participant.onboardingDetails.user_details.document_id.birth_date;

  const birthDate = rawBirthDate
    ? FormatDateMapper.hydrate(rawBirthDate).isoFormat
    : new Date(1900, 0, 0, 0, 0);
  const birthYear = birthDate.getFullYear();
  const { watch, getValues, handleSubmit, errors, control } = useForm({
    defaultValues: {
      employmentStatus: data?.employmentStatus || "",
      professionalPosition: data?.professionalPosition || "",
      companySeniority: data?.companySeniority || "",
      contractDateEnd: data?.contractDateEnd || "",
    },
  });

  const employmentStatusTypes =
    disableOthers === true
      ? employmentStatusTypeWithoutOtherConfig
      : employmentStatusTypesConfig;

  const employmentStatus = watch("employmentStatus");

  const {
    state: { dictionaryValues },
  } = useContext(ConfigContext);
  const isTemporaryEmployed =
    employmentStatus === employmentStatusTypes.TEMPORARY_EMPLOYED.value;

  const professionalPositionTypesConfig =
    employmentStatus === employmentStatusTypes.CIVIL_SERVANT.value
      ? professionalPositionTypesForCivilServantConfig
      : professionalPositionTypesForNonCivilServantConfig;

  const SetEmploymentStatusOptionsByIncomeType = (incomeType) => {
    switch (incomeType) {
      case netIncomeTypesConfig.SALARY.value:
        return employmentStatusTypes;
      case netIncomeTypesConfig.PENSION.value:
        return {
          PENSIONER: employmentStatusTypes.PENSIONER,
        };
      case netIncomeTypesConfig.OTHER_PAYMENT.value:
        return employmentStatusTypes;
      default:
        return employmentStatusTypes;
    }
  };

  const [state, setState] = React.useState({
    showPosition: false,
  });

  React.useEffect(() => {
    if (employmentStatus) {
      const showPosition = PROFESSIONAL_POSITION_FILTER.find(
        (element) => element === employmentStatus
      );
      setState((s) => ({
        ...s,
        showPosition: showPosition,
      }));
    }
  }, [employmentStatus]);

  const { showPosition } = state;

  return netIncomeType ? (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_PROFESSIONAL_DATA}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="PROFESSIONAL_DATA" />}
          subtitle={<Text tId="EMPLOYMENT_STATUS" />}
        />
        <FormControl fullWidth>
          <InputLabel id="employmentStatus">
            <Text tId="EMPLOYMENT_STATUS" />
          </InputLabel>
          <Controller
            name="employmentStatus"
            control={control}
            rules={{
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
            }}
            render={(props) => (
              <>
                <Select
                  MenuProps={{
                    MenuListProps: {
                      "aria-label": "Select employment status",
                    },
                  }}
                  onChange={(e) => props.onChange(e.target.value)}
                  value={props.value}
                  error={errors.employmentStatus ? true : false}
                >
                  {netIncomeType &&
                    objectTypesConfigToArray(
                      SetEmploymentStatusOptionsByIncomeType(netIncomeType)
                    ).map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.display}
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText style={{ color: palette.error.main }}>
                  {errors.employmentStatus?.message}
                </FormHelperText>
              </>
            )}
          />
        </FormControl>
        {showPosition && (
          <>
            <FormControl fullWidth>
              <InputLabel id="professionalPosition">
                <Text tId="PROFESSIONAL_POSITION" />
              </InputLabel>
              <Controller
                name="professionalPosition"
                control={control}
                rules={{
                  required: {
                    value: showPosition ? true : false,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                }}
                render={(props) => (
                  <>
                    <Select
                      MenuProps={{
                        MenuListProps: {
                          "aria-label": "Select professional position",
                        },
                      }}
                      onChange={(e) => props.onChange(e.target.value)}
                      value={props.value}
                      error={errors.professionalPosition ? true : false}
                      disabled={!showPosition}
                    >
                      {objectTypesConfigToArray(
                        professionalPositionTypesConfig
                      ).map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.display}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText style={{ color: palette.error.main }}>
                      {errors.professionalPosition?.message}
                    </FormHelperText>
                  </>
                )}
              />
            </FormControl>
            {isTemporaryEmployed ? (
              <InputDate
                allowKeyboardPicking={!isMobile}
                locale={dictionaryValues["DATE_LOCALE"]}
                emptyLabel={dictionaryValues["DATE_FORMAT_MONTH_YEAR"]}
                fullWidth
                variant="inline"
                openTo="year"
                autoOk
                views={["year", "month"]}
                format="MM/yyyy"
                label={<Text tId="COMPANY_YEAR" />}
                id="companySeniority"
                name="companySeniority"
                error={errors.companySeniority ? true : false}
                helperText={errors.companySeniority?.message}
                disableFuture={true}
                control={control}
                minDate={birthDate.setFullYear(birthYear + 18)}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                  validate: {
                    validate: (value) =>
                      validateKeyboardInputDate({
                        value,
                        disableFuture: true,
                        minDate: new Date(
                          birthYear + 18,
                          birthDate.getMonth(),
                          0,
                          0,
                          0,
                          0,
                          0
                        ),
                      }),
                  },
                }}
              />
            ) : (
              <InputDate
                allowKeyboardPicking={!isMobile}
                locale={dictionaryValues["DATE_LOCALE"]}
                emptyLabel={dictionaryValues["DATE_FORMAT_YEAR"]}
                fullWidth
                variant="inline"
                openTo="year"
                autoOk
                views={["year"]}
                format="yyyy"
                label={<Text tId="COMPANY_YEAR" />}
                id="companySeniority"
                name="companySeniority"
                error={errors.companySeniority ? true : false}
                helperText={errors.companySeniority?.message}
                disableFuture={true}
                control={control}
                minDate={birthDate.setFullYear(birthYear + 18)}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                  validate: {
                    validate: (value) =>
                      validateKeyboardInputDate({
                        value,
                        disableFuture: true,
                        minDate: new Date(birthYear + 18, 0, 0, 0, 0, 0, 0),
                      }),
                  },
                }}
              />
            )}
            {isTemporaryEmployed && (
              <InputDate
                allowKeyboardPicking={!isMobile}
                locale={dictionaryValues["DATE_LOCALE"]}
                emptyLabel={dictionaryValues["DATE_FORMAT_MONTH_YEAR"]}
                control={control}
                fullWidth
                variant="inline"
                openTo="year"
                autoOk
                views={["year", "month"]}
                format="MM/yyyy"
                label={<Text tId="CONTRACT_EXPIRATION" />}
                id="contractDateEnd"
                name="contractDateEnd"
                error={errors.contractDateEnd ? true : false}
                helperText={errors.contractDateEnd?.message}
                disablePast={true}
                rules={{
                  required: {
                    value: true,
                    message: <Text tId={"REQUIRED_FIELD"} />,
                  },
                  validate: {
                    validate: (value) =>
                      validateKeyboardInputDate({ value, disablePast: true }),
                  },
                }}
              />
            )}
          </>
        )}
      </Box>
      <Box>
        <MainButton onClick={handleSubmit(() => onSubmitForm(getValues()))}>
          <Text tId="CONTINUE" />
        </MainButton>
      </Box>
    </>
  ) : (
    <>
      <Box
        style={{
          height: "65vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress
          className={"transition-all"}
          size={90}
          thickness={1.5}
          color="secondary"
        />
      </Box>
    </>
  );
}
